import { SyncOutlined } from '@ant-design/icons';
import { type IWarehouseDetail } from '@mahawi/eshop-common/src/types';
import { Button, DatePicker, Popover, Select, Space, Typography } from 'antd';
import dayjs, { type Dayjs } from 'dayjs';
import React, { useState } from 'react';
import { useSelector } from 'react-redux';

import { useAppDispatch } from '../../hooks/use-app-dispatch';
import { type RootState } from '../../reducers';
import { warehouseOrdersSync } from '../../reducers/warehouse-order/reducer';
import { SPACING } from '../../theme/tokens';

const { Text } = Typography;

const DEFAULT_CHANGED_DAYS = 14;
const CHANGED_SINCE_FORMAT = 'YYYY-MM-DDTHH:mm:ss';

function SyncButton(): React.ReactElement {
  const dispatch = useAppDispatch();
  const warehouses = useSelector((s: RootState) => s.Warehouse.warehouses);
  const inProcess = useSelector((s: RootState) => s.WarehouseOrder.inProcess);
  const externalWarehouses: IWarehouseDetail[] = (warehouses ?? []).filter(
    (warehouse: IWarehouseDetail): boolean =>
      !warehouse.isInternal && warehouse.isActive,
  );

  const [isOpen, setIsOpen] = useState(false);
  const [warehouseUuids, setWarehouseUuids] = useState<string[]>([]);
  const [changedSince, setChangedSince] = useState<Dayjs>(
    dayjs().subtract(DEFAULT_CHANGED_DAYS, 'day').startOf('day'),
  );

  const selectedUuids: string[] =
    warehouseUuids.length > 0
      ? warehouseUuids
      : externalWarehouses.map(
          (warehouse: IWarehouseDetail): string => warehouse.uuid,
        );

  const content: React.ReactElement = (
    <Space orientation="vertical" size={SPACING.sm} style={{ width: 280 }}>
      <Text type="secondary">Warehouses</Text>
      <Select
        mode="multiple"
        style={{ width: '100%' }}
        value={selectedUuids}
        options={externalWarehouses.map((warehouse: IWarehouseDetail) => ({
          value: warehouse.uuid,
          label: warehouse.name,
        }))}
        onChange={setWarehouseUuids}
      />
      <Text type="secondary">Orders changed since</Text>
      <DatePicker
        style={{ width: '100%' }}
        allowClear={false}
        value={changedSince}
        disabledDate={(date: Dayjs): boolean => date.isAfter(dayjs(), 'day')}
        onChange={(date: Dayjs | null): void => {
          if (date) {
            setChangedSince(date.startOf('day'));
          }
        }}
      />
      <Button
        type="primary"
        block
        disabled={inProcess || selectedUuids.length === 0}
        onClick={(): void => {
          dispatch(
            warehouseOrdersSync({
              warehouseUuids: selectedUuids,
              changedSince: changedSince.format(CHANGED_SINCE_FORMAT),
            }),
          );
          setIsOpen(false);
        }}
      >
        Fetch orders
      </Button>
    </Space>
  );

  return (
    <Popover
      title="Sync warehouse orders"
      trigger="click"
      open={isOpen}
      onOpenChange={setIsOpen}
      content={content}
    >
      <Button icon={<SyncOutlined />}>Sync now</Button>
    </Popover>
  );
}

export default SyncButton;
